import { useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { FaArrowRight, FaRegCommentDots, FaWhatsapp } from "react-icons/fa";
import FaqAccordion from "@/components/UI/FaqAccordion";
import SeoHelmet from "@/components/UI/SeoHelmet";
import PageHero from "@/components/UI/PageHero";
import WhatsAppButton from "@/components/UI/WhatsAppButton";
import ActionLink from "@/components/UI/ActionLink";
import ArrowLink from "@/components/UI/ArrowLink";
import FeedbackForm from "@/components/Forms/FeedbackForm";
import { inputClass } from "@/components/Forms/FormField";
import { storeLabel, useStores } from "@/components/Forms/StoreSelect";
import market from "@/public/svg/market.svg";
import cart from "@/public/svg/cart.svg";
import login from "@/public/svg/login3.svg";
import bankCard from "@/public/svg/bank-card.svg";
import plantHand from "@/public/svg/plant_hand.svg";
import seoImg from "@/public/img/seo/kontakt.png";
import basket from "@/public/img/einkaufskorb.png";
import { WHATSAPP_CHANNEL_URL } from "@/data/contact";

const steps = [
    { icon: login, title: "Reinkommen", text: "Die Hinweise am Eingang zeigen dir, wie es geht." },
    { icon: cart, title: "Auswählen", text: "Nimm mit, was du für deinen Alltag brauchst." },
    { icon: bankCard, title: "Scannen & bezahlen", text: "Du scannst deine Produkte und bezahlst selbst." },
];

const faqs = [
    {
        question: "Wann ist mein Yobsti geöffnet?",
        answer: "Die aktuellen Öffnungszeiten findest du auf der Seite deines Standorts. Für die übernommenen Märkte starten wir an den entsprechend ausgewiesenen Standorten mit täglich 6–22 Uhr.",
    },
    {
        question: "Wird mein Markt wieder rund um die Uhr geöffnet sein?",
        answer: "Perspektivisch möchten wir wieder längere Öffnungszeiten bis hin zu 24/7 ermöglichen. Ob und wann das an deinem Standort möglich ist, steht noch nicht fest. Sobald sich die Öffnungszeiten ändern, informieren wir dich auf der Standortseite.",
    },
    {
        question: "Brauche ich die bisherige teo-App?",
        answer: "Für den Start als Yobsti ist keine App erforderlich. Der Zutritt erfolgt über Bankkarte. Bitte beachte die Hinweise am Eingang deines Marktes.",
    },
    {
        question: "Bleibt das Sortiment gleich?",
        answer: "Bei der Umstellung kann sich die Auswahl verändern. Wir möchten das Sortiment Schritt für Schritt an die Wünsche vor Ort anpassen. Vermisst du ein Produkt? Teile uns deinen Wunsch mit.",
    },
    {
        question: "Gibt es regionale Produkte?",
        answer: "Wir möchten passende regionale Anbieter schrittweise einbinden. Die Auswahl kann sich je Standort unterscheiden. Kennst du einen Anbieter, den wir ansprechen sollten? Empfiehl ihn uns.",
    },
    {
        question: "Wie erreiche ich euch bei einem Problem?",
        answer: "Schreib uns über WhatsApp und nenne den Standort sowie eine kurze Beschreibung. So können wir dein Anliegen besser zuordnen.",
    },
];

export default function CustomersPage() {
    const location = useLocation();
    const navigate = useNavigate();
    const { stores } = useStores();
    const [storeId, setStoreId] = useState("");
    const feedbackOpen = location.hash === "#feedback";

    const openFeedback = () =>
        navigate({ search: storeId ? `?markt=${storeId}` : "", hash: "#feedback" });

    return (
        <>
            <SeoHelmet
                title="Yobsti für Kunden – So funktioniert dein Einkauf"
                description="So kaufst du bei Yobsti ein, so erreichst du uns und so gestaltest du deinen Markt mit."
                image={seoImg}
                url="https://www.yobsti.de/fuer-kunden"
            />

            <PageHero
                eyebrow="Für Kunden"
                title={<>Dein Yobsti.<br />Dein Einkauf.</>}
                text="Lebensmittel direkt im Ort. Einfach einkaufen und deinen Markt mitgestalten."
                illustration={basket}
            >
                <div className="mt-8 flex flex-wrap gap-3">
                    <WhatsAppButton location="customers" />
                    <ActionLink to="#feedback" event="cta-click" eventData={{ target: 'feedback', location: 'customers-hero' }}>
                        <FaRegCommentDots className="h-5 w-5" aria-hidden="true" />
                        Feedback geben
                    </ActionLink>
                </div>
                <div className="mt-5">
                    <ArrowLink to="/standorte" event="cta-click" eventData={{ target: 'find-store', location: 'customers-hero' }}>Standort finden</ArrowLink>
                </div>
            </PageHero>

            <section id="einkauf" className="max-w-6xl mx-auto px-4 py-12 text-textColor scroll-mt-4">
                <h2 className="text-3xl sm:text-4xl font-extrabold mb-10">So einfach kaufst du ein.</h2>
                <ol className="grid grid-cols-1 md:grid-cols-3 md:divide-x divide-borderColor gap-y-10">
                    {steps.map((step, idx) => (
                        <li key={step.title} className="flex flex-col items-center text-center gap-3 px-6">
                            <span className="flex h-28 w-28 items-center justify-center rounded-full bg-primaryLight">
                                <img src={step.icon} alt="" className="h-14 w-14" />
                            </span>
                            <h3 className="text-xl font-bold">{idx + 1}. {step.title}</h3>
                            <p className="max-w-[16rem]">{step.text}</p>
                        </li>
                    ))}
                </ol>
            </section>

            <section className="max-w-6xl mx-auto px-4 text-textColor">
                <div className="grid grid-cols-1 lg:grid-cols-[1fr_minmax(0,22rem)] items-center gap-6 rounded-2xl bg-secondaryLight p-6 sm:p-10">
                    <div>
                        <h2 className="text-3xl sm:text-4xl font-extrabold">Was fehlt dir im Regal?</h2>
                        <p className="mt-3 text-lg">
                            Sortiment, Sauberkeit oder dein letzter Einkauf: Sag uns, was wir verbessern können.
                        </p>
                    </div>
                    <div>
                        <label htmlFor="feedback-teaser-store" className="mb-1 block font-medium">Dein Yobsti</label>
                        <select
                            id="feedback-teaser-store"
                            value={storeId}
                            onChange={(e) => setStoreId(e.target.value)}
                            className={inputClass}
                        >
                            <option value="">Standort auswählen</option>
                            {stores.map((store) => (
                                <option key={store.id} value={store.id}>{storeLabel(store.name)}</option>
                            ))}
                        </select>
                        <button
                            type="button"
                            onClick={openFeedback}
                            className="mt-3 inline-flex w-full items-center justify-center gap-2 rounded-full border-2 border-primary bg-primary px-5 py-3 font-semibold text-textColor transition hover:border-primaryDark hover:bg-primaryDark"
                        >
                            Zum Feedbackformular
                            <FaArrowRight className="h-3.5 w-3.5" aria-hidden="true" />
                        </button>
                    </div>
                </div>
            </section>

            {feedbackOpen && (
                <section id="feedback" className="max-w-3xl mx-auto px-4 pt-10 text-textColor scroll-mt-4" data-testid="feedback">
                    <h2 className="text-2xl font-bold mb-2">Dein Feedback macht deinen Yobsti besser.</h2>
                    <p className="mb-6">Wähle deinen Markt aus, damit wir dein Feedback richtig zuordnen können.</p>
                    <FeedbackForm />
                </section>
            )}

            <section className="max-w-6xl mx-auto px-4 py-10 grid grid-cols-1 md:grid-cols-2 gap-6 text-textColor">
                <div className="flex items-center gap-5 rounded-2xl border border-borderColor p-6">
                    <span className="flex h-16 w-16 flex-shrink-0 items-center justify-center rounded-full bg-primaryLight">
                        <FaWhatsapp className="h-8 w-8" aria-hidden="true" />
                    </span>
                    <div>
                        <h3 className="text-lg font-bold">Neuigkeiten aus deinem Yobsti</h3>
                        <p className="mb-1">Bleib über WhatsApp auf dem Laufenden.</p>
                        <ArrowLink href={WHATSAPP_CHANNEL_URL} event="whatsapp-channel-click" eventData={{ location: 'customers' }}>Zum WhatsApp-Kanal</ArrowLink>
                    </div>
                </div>
                <div className="flex items-center gap-5 rounded-2xl border border-borderColor p-6">
                    <span className="flex h-16 w-16 flex-shrink-0 items-center justify-center rounded-full bg-primaryLight">
                        <img src={plantHand} alt="" className="h-8 w-8" />
                    </span>
                    <div>
                        <h3 className="text-lg font-bold">Du kennst gute regionale Produkte?</h3>
                        <p className="mb-1">Empfiehl uns einen Anbieter aus deiner Nähe.</p>
                        <ArrowLink to="/fuer-lieferanten#empfehlen" event="cta-click" eventData={{ target: 'supplier-recommendation', location: 'customers' }}>Regionalen Anbieter empfehlen</ArrowLink>
                    </div>
                </div>
            </section>

            <section id="fragen" className="px-4 pb-12 scroll-mt-4">
                <FaqAccordion icon={market} title="Häufige Fragen" faqs={faqs} />
            </section>
        </>
    );
}
